import React, { useMemo, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
	Chart as ChartJS,
	BarElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";
import { formatPct } from "../lib/format";
import { getFacetTheme } from "../assets/facetTheme";
import "../styling/Facets.css";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const EMPTY_SELECTION = new Set();

export default function FacetBar({
	data = [],
	loading = false,
	filters,
	setFilters,
	field,
	grouped = false,
	category
}) {
	const [error] = useState(null);

	const theme = getFacetTheme(category);
	const BAR_COLOR = theme.primary;
	const DIM = theme.dim;

	const GRID = "#E9EDF2";
	const AXIS = "#97A3B6";

	const ONGOING_COLOR = theme.palette[0];
	const ENDED_COLOR = theme.palette[1] ?? theme.primary;
	const NA_COLOR = theme.dim;

	const labels = useMemo(() => data.map((d) => d.name), [data]);
	const counts = useMemo(() => data.map((d) => d.count), [data]);
	const sel = filters.get(field) || EMPTY_SELECTION;

	const isGrouped = grouped && data.length > 0 && data[0].ongoing !== undefined;

	const totalCount = useMemo(() => {
		if (isGrouped) return 0;
		return counts.reduce((a, b) => a + (Number(b) || 0), 0);
	}, [isGrouped, counts]);

	const datasets = useMemo(() => {
		if (isGrouped) {
			return [
				{
					label: "Ongoing",
					data: data.map((d) => d.ongoing || 0),
					backgroundColor: labels.map((label) =>
						sel.size > 0 && !sel.has(label) ? DIM : ONGOING_COLOR
					),
					borderRadius: 4,
					maxBarThickness: 24,
				},
				{
					label: "Ended",
					data: data.map((d) => d.ended || 0),
					backgroundColor: labels.map((label) =>
						sel.size > 0 && !sel.has(label) ? DIM : ENDED_COLOR
					),
					borderRadius: 4,
					maxBarThickness: 24,
				},
				{
					label: "N/A",
					data: data.map((d) => d.na || 0),
					backgroundColor: labels.map((label) =>
						sel.size > 0 && !sel.has(label) ? DIM : NA_COLOR
					),
					borderRadius: 4,
					maxBarThickness: 24,
				},
			];
		}

		const bgColors = !sel.size
			? Array(labels.length).fill(BAR_COLOR)
			: labels.map((lab) => (sel.has(lab) ? BAR_COLOR : DIM));

		return [
			{
				label: "Count",
				data: counts,
				backgroundColor: bgColors,
				borderRadius: 6,
				maxBarThickness: 38,
				categoryPercentage: 0.8,
				barPercentage: 0.9,
			}
		];
	}, [isGrouped, data, labels, counts, sel, BAR_COLOR, DIM, ONGOING_COLOR, ENDED_COLOR, NA_COLOR]);

	const chartData = useMemo(
		() => ({
			labels,
			datasets,
		}),
		[labels, datasets]
	);

	const handlePickLabel = useCallback(
		(label) => {
			setFilters((prev) => {
				const next = new Map(prev);
				const curr = new Set(next.get(field) || []);
				if (curr.has(label)) curr.delete(label);
				else curr.add(label);
				next.set(field, curr.size > 0 ? curr : new Set());
				return next;
			});
		},
		[field, setFilters]
	);

	const chartOptions = useMemo(() => ({
		responsive: true,
		maintainAspectRatio: false,
		animation: false,
		transitions: { active: { animation: { duration: 0 } } },
		layout: { padding: { top: 4, right: 6, bottom: 6, left: 6 } },

		onClick: (_evt, active) => {
			if (active?.length) {
				const label = labels[active[0].index];
				handlePickLabel(label);
			}
		},

		onHover: (e, a) => {
			e.native.target.style.cursor = a.length ? "pointer" : "default";
		},

		scales: {
			x: {
				grid: { display: true, color: GRID, drawBorder: false },
				ticks: { color: AXIS, maxRotation: 45, autoSkip: false },
				stacked: false,
			},
			y: {
				beginAtZero: true,
				grid: { color: GRID, drawBorder: false },
				ticks: { precision: 0, color: AXIS },
				stacked: false,
			},
		},

		plugins: {
			legend: {
				display: isGrouped,
				position: "top",
				align: "end",
				labels: {
					boxWidth: 12,
					padding: 8,
					font: { size: 11 },
				},
			},
			tooltip: {
				intersect: false,
				callbacks: {
					title: () => null,
					label: (ctx) => {
						const val = ctx.parsed?.y ?? ctx.raw ?? 0;

						if (isGrouped) {
							return ` ${ctx.dataset.label}: ${val}`;
						}

						const pct = totalCount ? formatPct((val / totalCount) * 100) : "0%";
						return ` ${ctx.label}: ${val} (${pct})`;
					},
				},
			},
		},
	}), [labels, isGrouped, totalCount, handlePickLabel]);

	if (loading) {
		return (
			<div className="facet-loading">
				<div className="spinner"></div>
				<div className="loading-text">Loading chart...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="facet-error">
				Error: {error}
			</div>
		);
	}

	return (
		<div className="chart-fill bar facet-enter">
			<Bar data={chartData} options={chartOptions} />
		</div>
	);
}